// Product-specific print content. Options are matched by name, never by position.
export function derivePrintContentState(mode, config, variantId, designSide) {
  const variant = config.variants.find((item) => String(item.id) === String(variantId));
  if (!variant) return { sides: [], designSideRequired: false };
  const placementIndex = config.options.indexOf('Print Placement');
  const placement = placementIndex < 0 ? '' : variant.options[placementIndex];
  if (mode === 'custom') {
    if (placementIndex < 0 || placement === 'Outside Print') return { sides: ['Outside'], designSideRequired: false };
    if (placement === 'Inside Print') return { sides: ['Inside'], designSideRequired: false };
    if (placement === 'Double-Sided Print') return { sides: ['Outside', 'Inside'], designSideRequired: false };
  }
  if (mode === 'ready' && placement === 'Double-Sided Print') {
    return {
      sides: designSide === 'Outside' ? ['Inside'] : designSide === 'Inside' ? ['Outside'] : [],
      designSideRequired: true,
    };
  }
  return { sides: [], designSideRequired: false };
}

export function validatePrintContent(state, values) {
  if (state.designSideRequired && !values.designSide) return 'designSide';
  for (const side of state.sides) {
    if (!values[side]?.text?.trim() && !values[side]?.file) return side;
  }
  return null;
}

const ElementBase = globalThis.HTMLElement || class {};

class KpPrintContent extends ElementBase {
  connectedCallback() {
    // A full product morph can connect this block before its new buy form exists.
    queueMicrotask(() => this.initialize());
  }

  initialize() {
    if (!this.isConnected || this.abortController) return;
    this.config = JSON.parse(this.querySelector('[data-kp-print-config]').textContent);
    this.section = this.closest('.shopify-section');
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form || !this.section) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.addEventListener('input', () => this.syncValidity(), { signal });
    this.addEventListener('change', () => this.sync(this.currentVariantId), { signal });
    this.addEventListener('click', this.onContentClick, { signal });
    this.form.addEventListener('submit', this.onSubmit, { capture: true, signal });
    this.section.addEventListener('variant:update', this.onVariantUpdate, { signal });
    this.section.addEventListener('click', this.onStickyClick, { capture: true, signal });
    window.addEventListener('popstate', this.onHistoryChange, { signal });
    this.setAttribute('data-enhanced', '');
    this.sync(this.form.querySelector('[name="id"]')?.value);
    const fallback = this.querySelector('[data-fallback]');
    if (fallback && this.config.variants.some((variant) => String(variant.id) === String(this.currentVariantId))) {
      fallback.hidden = true;
    }
  }

  updateFileRow(file) {
    const row = file?.closest('[data-file-row]');
    if (!row) return;
    const hasFile = Boolean(file.files?.length);
    if (hasFile && !file.disabled) file.name = file.dataset.filePropertyName;
    else file.removeAttribute('name');
    const name = row.querySelector('[data-file-name]');
    const remove = row.querySelector('[data-file-remove]');
    if (name) name.textContent = hasFile ? file.files[0].name : 'No file selected';
    if (remove) remove.hidden = !hasFile;
  }

  clearFile(file) {
    if (!file) return;
    file.value = '';
    this.updateFileRow(file);
    this.syncValidity();
  }

  onContentClick = (event) => {
    const remove = event.target.closest('[data-file-remove]');
    if (remove) {
      this.clearFile(remove.closest('[data-file-row]')?.querySelector('[data-content-file]'));
      return;
    }
    const file = event.target.closest('[data-content-file]');
    if (file?.files?.length) this.clearFile(file);
  };

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = undefined;
  }

  onVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    if (!this.section.contains(event.target)) return;
    this.sync(event.detail?.resource?.id);
  };

  onHistoryChange = () => {
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    // The form ID is what Shopify will actually add. A URL alone may have changed
    // before the native picker/form has resolved the corresponding variant.
    this.sync(this.form.querySelector('[name="id"]')?.value || fromUrl);
  };

  selectedDesignSide() {
    return this.querySelector('[data-design-side]:checked:not(:disabled)')?.value || '';
  }

  sync(variantId) {
    this.currentVariantId = variantId;
    const designSide = this.querySelector('[data-design-side]:checked')?.value || '';
    this.state = derivePrintContentState(this.dataset.mode, this.config, variantId, designSide);
    const choice = this.querySelector('[data-design-choice]');
    if (choice) choice.hidden = !this.state.designSideRequired;
    for (const radio of this.querySelectorAll('[data-design-side]')) {
      radio.disabled = !this.state.designSideRequired;
      radio.required = this.state.designSideRequired;
    }
    for (const group of this.querySelectorAll('[data-side]')) {
      const active = this.state.sides.includes(group.dataset.side);
      group.hidden = !active;
      for (const input of group.querySelectorAll('input, textarea')) {
        input.disabled = !active;
        if (!active && input.setCustomValidity) input.setCustomValidity('');
      }
      const heading = group.querySelector('[data-side-heading]');
      if (heading) heading.textContent = this.dataset.mode === 'ready' ? group.dataset.readyHeading : `${group.dataset.side} content`;
      const description = group.querySelector('[data-side-description]');
      if (description) description.textContent = group.dataset.readyDescription;
      this.updateFileRow(group.querySelector('[data-content-file]'));
    }
    this.syncValidity();
  }

  syncValidity() {
    if (!this.state) return null;
    const values = { designSide: this.selectedDesignSide() };
    for (const group of this.querySelectorAll('[data-side]')) {
      const text = group.querySelector('[data-content-text]');
      const file = group.querySelector('[data-content-file]');
      values[group.dataset.side] = {
        text: text?.value || '',
        file: Boolean(file?.files?.length),
      };
      text?.setCustomValidity(group.hidden || text.value.trim() || file?.files?.length
        ? '' : `Enter ${group.dataset.side.toLowerCase()} text or upload a file.`);
    }
    return validatePrintContent(this.state, values);
  }

  onSubmit = (event) => {
    const error = this.syncValidity();
    if (!error) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const target = error === 'designSide'
      ? this.querySelector('[data-design-side]')
      : this.querySelector(`[data-side="${error}"] [data-content-text]`);
    target?.focus();
    target?.reportValidity();
  };

  onStickyClick = (event) => {
    const sticky = event.target.closest('sticky-add-to-cart');
    if (!sticky || !event.target.closest('.sticky-add-to-cart__button')) return;
    if (String(sticky.dataset.productId) !== this.dataset.productId) return;
    // Horizon's sticky button proxies a click on this same form's main button.
    // Intercept an invalid click before its independent success animation runs.
    this.onSubmit(event);
  };
}

if (globalThis.customElements && !customElements.get('kp-print-content')) {
  customElements.define('kp-print-content', KpPrintContent);
}
